<template>
    <div>
        <div class="row">
            <div v-if="!canSelectProduct">
                <div class="col-12" v-if="showRemainingPackageQuantity">
                    <div class="alert alert-info" v-if="hasRemainingPackageQuantity">
                        <p class="mb-0"><strong>{{ $t('Pakke') }}:</strong></p>
                        {{ $t('Vi har registrert pakke på deg i denne klassen. Du har {remainingCount} ubrukte kjøretimer tilgjengelig i pakken din.', {remainingCount: getRemainingPackageQuantity }) }}
                        <span v-if="scheduledLessonCount > 0"><br>{{ $t('Totalt {remainingCount} resterende hvorav {scheduledCount} er bestilt men ikke bekreftet.', { remainingCount: remainingPackageQuantity, scheduledCount: scheduledLessonCount}) }}</span>
                    </div>
                    <div class="alert alert-warning" v-if="!hasRemainingPackageQuantity">
                        <p class="mb-0"><strong>{{ $t('Pakke') }}:</strong></p>
                        {{ $t('Vi har registrert pakke på deg i denne klassen, men du har brukt opp alle kjøretimene.') }}
                    </div>
                </div>
            </div>

            <div class="col-md-6 mb-md-0 order-1 order-md-0">
                <p class="font-sm text-elev-blue-dim mb-2"><strong>{{ $t('Dato') }}</strong></p>
                <div class="mb-2">
                    <FullCalendar
                        ref="fullCalendar"
                        :options="calendarOptions"
                    />
                </div>
            </div>
            <div class="col-md-6 order-0 order-md-1" id="booking_slots_title">
                <div>
                    <ClassSelect @loaded="setClassSelectLoaded" @change="updateSelectedClass" :refresh="false"></ClassSelect>
                    <ProductSelect v-show="selectsLoaded" v-if="canSelectProduct" @change="updateSelectedProduct" @loaded="productSelectLoaded = true"></ProductSelect>
                    <TeacherSelect v-show="selectsLoaded" v-if="canSelectTeacher && !isCourse" @change="updateSelectedTeacher" @loaded="teacherSelectLoaded = true"></TeacherSelect>
                </div>

                <div class="d-md-block d-none" v-if="showDaySlots">
                    <DaySlots @goToDate="goToDate" :isLoadingSlotsForDate="isLoadingSlotsForDate" :canSelectTeacher="canSelectTeacher && !isCourse" :selectedDate="state.formattedDate"></DaySlots>
                </div>
            </div>
            <div class="col-md-6 order-3 offset-md-6 d-block d-md-none" v-if="showDaySlots">
                <DaySlots @goToDate="goToDate" :isLoadingSlotsForDate="isLoadingSlotsForDate" :canSelectTeacher="canSelectTeacher && !isCourse" :selectedDate="state.formattedDate"></DaySlots>
            </div>
        </div>
        <div class="row">
            <div class="col-12" v-if="selectedSlot">
                <BookableSlot :slotData="selectedSlot" :date="getDate" v-if="selectedSlot.available" :autoApproveBooking="autoApproveBooking" :terms="getBookingTerms"></BookableSlot>
                <SlotDetails :slotData="selectedSlot" :date="getDate" v-if="!selectedSlot.available" :autoApproveBooking="autoApproveBooking" :terms="getBookingTerms"></SlotDetails>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, useAttrs } from 'vue';
import Store from '../../state/calendarStore';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import list from '@fullcalendar/list';
import interaction from '@fullcalendar/interaction';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import Api from '../../api/Calendar';
import DaySlots from './DaySlots';
import BookableSlot from './BookableSlot';
import SlotDetails from './SlotDetails';
import ClassSelect from '../classSelect/ClassSelect';
import TeacherSelect from '../teacherSelect/TeacherSelect';
import ProductSelect from '../productSelect/ProductSelect';

const attrs = useAttrs()
const props = defineProps(['maxBookable', 'today', 'iscontact', 'remainingPackageQuantity', 'scheduledLessonCount', 'autoApproveBooking', 'hasBookingTerms']);

const fullCalendar = ref(null);
const classId = ref(null);
const productSelectLoaded = ref(false);
const teacherSelectLoaded = ref(false);
const classSelectLoaded = ref(false);
const daySlotsHidden = ref(false);

const canSelectTeacher = ref(attrs.canselectteacher);
const canSelectProduct = ref(attrs.canselectproduct);
const isCourse = ref(false);

const calendarOptions = {
    initialView: 'dayGridMonth',
    firstDay: 1,
    plugins: [dayGridPlugin, list, interaction, bootstrap5Plugin],
    locale: window.fullcalendarLocale,
    headerToolbar: {
        start: 'prev',
        center: 'title',
        end: 'next'
    },
    buttonIcons: {
        prev: 'chevron-left',
        next: 'chevron-right',
        prevYear: 'chevron-double-left',
        nextYear: 'chevron-double-right'
    },
    validRange: {
        'start': attrs.startdate,
        'end': attrs.enddate,
    },
    dayCellDidMount: rendered,
    themeSystem: 'standard', // set to standard because of icons
    weekLabel: $t('Uke'),
    height: 'auto',
    unselectAuto: false,
    dayHeaderContent: columnHeaderFormat,
    dateClick: goToDate,
};

const state = Store.state;

// Computed
const getDate = computed(() => Store.getCurrentDate());
const getBookingTerms = computed(() => Store.getBookingTerms());
const isLoadingSlotsForDate = computed(() => Store.getIsLoadingSlotsForDate());
const showRemainingPackageQuantity = computed(() => props.remainingPackageQuantity != null);
const selectedSlot = computed(() => Store.getSelectedSlot());
const getRemainingPackageQuantity = computed(() => {
    const remaining = props.remainingPackageQuantity === null ? 0 : props.remainingPackageQuantity;
    return (remaining - props.scheduledLessonCount);
});
const hasRemainingPackageQuantity = computed(() => getRemainingPackageQuantity > 0);
const selectsLoaded = computed(() => {
    const productsLoaded = !canSelectProduct.value || productSelectLoaded.value;
    const teachersLoaded = !canSelectTeacher.value || teacherSelectLoaded.value;
    const classesLoaded = classSelectLoaded.value;
    return productsLoaded && classesLoaded && teachersLoaded;
});
const showDaySlots = computed(() => {
    if (daySlotsHidden.value) {
        return false;
    }
    if (classId.value === null) {
        return false;
    }
    if (canSelectProduct.value && Store.getProductId() === null) {
        return false;
    }
    return true;
});

onMounted(() => {
    const actions = fullCalendar.value.getApi();
    Store.goToDate(props.today);
    Store.state.remainingPackageQuantity = props.remainingPackageQuantity;
    Store.state.scheduledLessonCount = props.scheduledLessonCount;
    Store.setCalendarActions(actions);

    if (!canSelectProduct.value) {
        // Product select will trigger a next available day query when loaded
        Store.getNextAvailableDay();
    }
});

watch(selectedSlot, (newVal) => {
    const bookingOverlay = document.getElementById('booking-overlay');
    if (newVal) {
        window.toggleOverlay(bookingOverlay, true);
    } else {
        window.toggleOverlay(bookingOverlay, false);
    }
});

function goToDate({ dateStr, dayEl }) {
    if (dayEl.className.indexOf('fc-day-past') !== -1) {
        return;
    }
    if (isLoadingSlotsForDate.value) {
        Store.goToDate(Store.getCurrentDate());
        return;
    }
    dayEl.classNames += 'fc-highlight';
    Store.goToDate(dateStr);
    Store.getEventsForDate(dateStr);
}

function rendered({ el, date }) {
    if (slotIsPast(el) || slotIsDisabled(el) || slotIsTooFarInFuture(el)) {
        el.className += ' fc-not-bookable';
    }
}

function slotIsPast(el) {
    return el.className.indexOf('fc-day-past') !== -1;
}

function slotIsDisabled(el) {
    return el.className.indexOf('fc-disabled-day') !== -1;
}

function slotIsTooFarInFuture(el) {
    return el.dataset.date > props.maxBookable;
}

function columnHeaderFormat(date) {
    const dayName = date.text.replace('.', '');
    return dayName.substring(0, 1).toUpperCase() + dayName.substring(1);
}

function updateSelectedTeacher(teacherId) {
    Store.setTeacherId(teacherId);
    if (canSelectProduct.value && Store.getProductId() === null) {
        return;
    }
    Store.setDate(props.today);
    Store.getNextAvailableDay();
}

function updateSelectedClass(data) {
    classId.value = data.id;
    productSelectLoaded.value = false;
    teacherSelectLoaded.value = false;

    canSelectProduct.value = false;
    canSelectTeacher.value = false;

    daySlotsHidden.value = true;
    Api.GetSelectOptions(data.id).then(res => {
        canSelectProduct.value = res.canSelectProduct;
        canSelectTeacher.value = res.canSelectTeacher;
        updateSelectedProduct({ productId: null });
        daySlotsHidden.value = false;
    });
}
function updateSelectedProduct(product) {
    isCourse.value = [3,35].includes(product.productType);
    Store.setDate(props.today)
    Store.setProductId(product.productId);
    Store.getNextAvailableDay();

}

function setClassSelectLoaded(data) {
    if (data.id !== null) {
        classId.value = data.id;
    }
    classSelectLoaded.value = true;
}
</script>
